/**
 * Main level-editor screen for building and configuring SIM levels.
 *
 * @module components/sim/createLevels/CreateLevels
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import {connect} from "react-redux";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    LinearProgress,
    Paper,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import EditIcon from "@material-ui/icons/Edit";
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import MicOutlinedIcon from '@material-ui/icons/MicOutlined';
import {useLocation} from "react-router-dom";
import {
    addLevels,
    addNewLevels,
    decreaseStep,
    fetchAutoPopulate,
    fetchCharacterList,
    fetchCurrentSim,
    fetchSimDetails,
    increaseStep,
    uploadLevels,
} from "../../../redux/sims/actions";
import Characters from "./Characters.jsx";
import CharacterData from "./CharacterData.jsx";
import TopTableToolbar from "./TopTableToolbar.jsx";
import GenerateSim from './GenerateSim.jsx';
import Levels from './Levels.jsx';
import Details from "../Details.jsx";
import {
    addLevelScript,
    deleteScript,
    duplicateSim,
    editLevels,
} from "../../../services/result.service";
import {useStyle} from "./CreateLevels.js";
import {DndProvider} from "react-dnd";
import {HTML5Backend} from 'react-dnd-html5-backend';
import DropZone from '../dragDrop/DropZone.jsx';
import '../dragDrop/style.css';
import {handleMoveSidebarComponentIntoParent, handleMoveWithinParent} from "../dragDrop/helpers";
import {DragDropContext, Draggable, Droppable} from "react-beautiful-dnd";
import LevelOptions from "./LevelOptions";
import AudioPlaylist from "../../common/AudioPlaylist";
import {clearAudioStorage} from "../../../utils/audioFetch";

/**
 * A custom hook that provides a URLSearchParams instance initialized with the current query
 * parameters from the URL. It utilizes the `useLocation` hook to access the location's search
 * string.
 *
 * @function
 * @returns {URLSearchParams} An instance of URLSearchParams containing the current query
 *     parameters.
 */
const useQuery = () => new URLSearchParams(useLocation().search);

/**
 * Determines the background color based on the input character type.
 *
 * @param {string} character - The type of character for which the background color is determined.
 * Possible values are "coach", "agent", "customer", "other". If an invalid or unspecified value
 * is provided, the default background color is returned.
 *
 * @returns {string} The hexadecimal color code representing the background color associated
 * with the given character type. Defaults to "#882647" for invalid or unspecified inputs.
 */
const getBackground = (character) => {
    let background;
    switch (character) {
        case "coach":
            background = "#882647";
            break;
        case "agent":
            background = "#EB6D20";
            break;
        case "customer":
            background = "#2694D3";
            break;
        case "other":
            background = "#2CB174";
            break;
        default:
            background = "#882647";
    }
    return background;
};

/**
 * A function component responsible for creating and managing levels within a
 *      simulation. This includes initializing, updating, and handling levels
 *      and their associated data.
 *
 * @param {Object} props - The required properties to set up the CreateLevels component.
 * @param {Object} props.newSim - The simulation data used to initialize levels.
 * @param {Function} props.fetchCurrentSim - A function to fetch current simulation data based on
 *     simulation ID.
 * @param {Object} props.currentSim - The current simulation data.
 * @param {Function} props.decreaseStep - A callback function to decrease the current progress step
 *     of the simulation wizard.
 * @param {Function} props.increaseStep - A callback function to increase the current progress step
 *     of the simulation wizard.
 * @param {Function} props.addLevels - A callback function to add new levels to the simulation.
 * @param {Function} props.uploadLevels - A callback function to upload levels for the simulation.
 * @param {Function} props.fetchAutoPopulate - A function to fetch auto-populate data for
 *     simulation levels.
 * @param {Object[]} props.tabs - The simulation tabs data.
 * @param {Function} props.fetchSimDetails - A function to fetch additional simulation details by
 *     simulation ID.
 * @param {Object} props.status - The current status of the simulation process.
 *
 * @returns {React.Component} A React functional component that manages simulation levels, their
 *     associated scripts, data states, and interaction handlers.
 *
 * @description
 * CreateLevels is a dynamic component that handles all aspects of adding, updating, and
 *     maintaining simulation levels. It leverages React hooks such as `useState` and `useEffect`
 *     to manage state and side effects, ensuring a seamless user experience. The component
 *     encapsulates logic for:
 *
 * - Initialization of level arrays during rendering based on simulation data.
 * - Handling user interactions, such as updating script contents, keywords, and associated
 *     metadata.
 * - Managing alerts and success/error notifications within the level creation process.
 * - Fetching and synchronizing simulation details from the provided APIs via callback functions.
 * - Maintaining real-time feedback and configurations for scripts within levels.
 */
const CreateLevels = ({
    newSim,
    fetchCurrentSim,
    currentSim,
    decreaseStep,
    increaseStep,
    addLevels,
    uploadLevels,
    fetchAutoPopulate,
    tabs,
    fetchSimDetails,
    status,
}) => {
    const classes = useStyle();
    const query = useQuery();
    const [isLoading, setIsLoading] = useState(true);
    const [simGenerated, setSimGenerated] = useState(false);
    const [data, setData] = useState({...newSim});
    const [open, setOpen] = React.useState(false);
    const [levelSelect, setLevelSelect] = React.useState(false);
    const [openAlert, setOpenAlert] = React.useState(false);
    const [openSuccessAlert, setSuccessAlert] = React.useState(false);
    const [currentIndex, setIndex] = React.useState(0);
    const [alertMesage, setAlertMessage] = React.useState("");
    const [alertType, setAlertType] = React.useState("error");
    const [load, setLoad] = React.useState(false);
    const [flag, setFlag] = useState(true);
    const [totalLevel, setTotalLevel] = useState(0);
    const [deleted, setDeleted] = React.useState(false);
    const [selectedClip, setSelectedClip] = React.useState(0);
    const [importLevel, setImportLevel] = React.useState(false);
    const [levelName, setLevelName] = React.useState(
        data.levels[currentIndex] ? data.levels[currentIndex].name : ""
    );
    const [timePlay, setTimePlay] = useState(0);
    const [currentAudio, setCurrentAudio] = useState({});
    const [audioList, setAudioList] = useState([]);
    const [generatingAudio, setGeneratingAudio] = useState(false);

    const clipRef = useRef(null);

    /**
     * Callback function to initialize the `levels` array within the simulation data object.
     *
     * This function checks if the levels array in the `data` object is empty and populates it with
     * default level configurations if needed. It clones the current `data` object, ensures that
     * the levels array is populated with predefined level data (e.g., "Watch and Learn"), and
     * updates the `data` state with the modified array.
     *
     * Dependencies:
     * - Ensures to use the current `data` state.
     * - Updates the `data` state via `setData`.
     *
     * Preconditions:
     * - `data` must be an object containing a `levels` array.
     * - Each level object created follows a specified schema with predefined properties.
     *
     * Postconditions:
     * - If the levels array is empty, the function populates it with one or more predefined level
     * objects.
     * - The `data` state is updated with the modified `levels` array after initialization.
     *
     * This function is memoized using `React.useCallback` to prevent unnecessary re-creations
     * between renders.
     */
    const initializeLevelArray = React.useCallback(() => {
        const simData = {...data};
        const simLevels = ["Watch and Learn"];
        if (simData.levels.length === 0) {
            let index = 1;
            for (const level of simLevels) {
                simData.levels.push({
                    _id: undefined,
                    name: level,
                    order: index - 1,
                    real_time_feedback: false,
                    natural_lenguage: data.natural_lenguage,
                    send_to_snowfly: false,
                    hint_text: false,
                    keyword_score: true,
                    scripts: [],
                });
                index += 1;
            }
        }
        setData(simData);
    }, [data]);

    useEffect(() => {
        if (query.get("simId") && data.fetchStatus === true && data.levels.length) {
            setIsLoading(false);
            localStorage.setItem('sims_id', query.get("simId"))
        }
    }, [data, query]);

    useEffect(() => {
        if (data.id && currentSim && Object.keys(currentSim).length === 0) {
            fetchCurrentSim(data.id);
        }
    }, [fetchCurrentSim, data.id, currentSim]);



    useEffect(() => {
        fetchAutoPopulate(query.get("simId"));
    }, [fetchAutoPopulate]);

    useEffect(() => {
        if (selectedClip) {
            clipRef.current.scrollIntoView(false);
        }
    }, [clipRef, selectedClip]);

    useEffect(() => {
        if (data.id && data.levels.length <= 0) {
            initializeLevelArray();
        }
    }, [data.id, data.levels, initializeLevelArray]);

    useEffect(() => {
        if (data?.levels?.length) {
            setTotalLevel(data.levels.length);
            setLevelSelect(false);
            if (generatingAudio) {
                return;
            }
            let audios = [];
            if (data.levels[currentIndex]?.scripts?.length) {
                for (const script of data.levels[currentIndex].scripts) {
                    audios.push({
                        id: script._id,
                        audio: script.audio ?? script.audio_url ?? null
                    });
                }
                setAudioList(audios);
            }
        }
    }, [data, currentIndex, generatingAudio]);

    useEffect(() => {
        setData({...newSim});
    }, [newSim]);

    useEffect(
        () => {
            if (query.get("simId") && !data.id) {
                fetchSimDetails(query.get("simId"));
            } else {
                setIsLoading(false);
            }

            return () => {
                clearAudioStorage();
            }
        },
        []
    );

    /**
     * A function that handles the event of opening a dialog or modal.
     * This function sets the current level name based on the provided
     * data object and the current index, and toggles the state
     * to open the dialog or modal.
     *
     * @function handleClickOpen
     * @returns {void} Does not return a value.
     */
    const handleClickOpen = () => {
        setLevelName(data.levels[currentIndex].name);
        setOpen(true);
    };

    /**
     * Closes the current dialog or component by resetting the `levelName` state
     * to an empty string and setting the `open` state to false.
     *
     * It is typically used as a callback function to handle the closing logic of a
     * modal, dialog, or any UI component requiring state updates upon closure.
     */
    const handleClose = () => {
        setLevelName('');
        setOpen(false);
    };

    /**
     * Updates the data of a specific script within a simulation level and character,
     * and sends updates to the server as needed.
     *
     * @async
     * @function
     * @name updateData
     * @param {number} index - The index of the script within the current simulation level to be
     *     updated.
     * @param {Object} characterData - The new data for the script to be updated.
     * @param {string} characterData.script - The script text to update.
     * @param {Array<string>} characterData.keywords - The keywords associated with the script.
     * @param {string} characterData.hint_text - Hint text for the script.
     * @param {boolean} characterData.hide_text - Indicator to hide the hint text.
     * @param {boolean} characterData.real_time_feedback - Indicates whether real-time feedback is
     *     enabled.
     * @param {boolean} characterData.send_to_snowfly - Indicates whether to send this script's
     *     data to Snowfly.
     * @param {boolean} characterData.autopopulate_timer - Indicates whether autopopulate timer is
     *     enabled.
     * @param {Object} characterData.autopopulate_data - Data to be used for autopopulate
     *     functionality.
     * @param {string} characterData.audio_url - URL of the associated audio file for the script.
     * @param {Array<Object>} characterData.tests - Tests associated with the script.
     * @param {string} characterData.sim_character_id - The ID of the associated simulation
     *     character.
     * @param {string} characterData.agent_recorded_audio - The agent's recorded audio for the
     *     script, if any.
     * @param {number} characterData.overalConfidence - Overall confidence score for the script.
     * @throws {Error} If any required data for updating is missing or undefined.
     * @description
     * This function modifies a specified script within the "levels" data structure of a
     *     simulation.
     * It updates a variety of script properties provided in the `characterData` parameter and
     * prepares the updated script data to be sent to the server. If the specified script index
     * does not exist, the function returns without any updates. Certain additional logic is
     * applied for initializing a script if required, and the local state is updated.
     */
    const updateData = async (index, characterData) => {
        const simData = {...data};
        const requestData = {};
        const addIntialScript = async (requestData) => {
            const response = await addLevelScript(requestData);
            simData.levels[currentIndex].scripts[index]._id = response.sim_script_id;
        };
        if (!simData?.levels[currentIndex]?.scripts[index]) {
            return;
        }
        simData.levels[currentIndex].scripts[index].script = characterData.script;
        simData.levels[currentIndex].scripts[index].keywords = characterData.keywords;
        simData.levels[currentIndex].scripts[index].hint_text = characterData.hint_text;
        simData.levels[currentIndex].scripts[index].hide_text = characterData.hide_text;
        simData.levels[currentIndex].scripts[index].real_time_feedback =
            characterData.real_time_feedback;
        simData.levels[currentIndex].scripts[index].natural_lenguage = data.natural_lenguage;
        simData.levels[currentIndex].scripts[index].send_to_snowfly = characterData.send_to_snowfly;
        simData.levels[currentIndex].scripts[index].tests = characterData.tests;
        requestData.is_level = false;
        requestData._id = simData.levels[currentIndex].scripts[index]._id;
        requestData.sim_level_id = simData.levels[currentIndex]._id;
        requestData.sim_id = simData.id;
        requestData.script = characterData.script;
        requestData.sim_character_id = characterData.sim_character_id;
        requestData.keywords = characterData.keywords;
        requestData.tests = characterData.tests;
        requestData.hint_text = characterData.hint_text;
        requestData.hide_text = characterData.hide_text;
        requestData.autopopulate_timer = characterData.autopopulate_timer;
        requestData.autopopulate_data = characterData.autopopulate_data;
        requestData.audio_url = characterData.audio_url;
        requestData.order = simData.levels[currentIndex].scripts[index].order;
        requestData.real_time_feedback = characterData.real_time_feedback;
        requestData.natural_lenguage = data.natural_lenguage;
        requestData.agent_recorded_audio = characterData.agent_recorded_audio;
        requestData.overalConfidence = characterData.overalConfidence;
        await addIntialScript(requestData);
        setData(simData);
    };

    /**
     * Updates the current level in the simulation data and resets the play time.
     *
     * @function Level
     * @param {Object} cLevel - The current level object that contains information about the level.
     * @param {number} cLevel.order - The order of the current level to be matched within the
     *     simulation data levels.
     * @description This function identifies the index of the provided level in the simulation
     *     data's levels list based on the `order` property. It then updates the index to the
     *     matched level's position and resets the play time to zero.
     */
    const Level = (cLevel) => {
        const simData = {...data};
        const index = simData.levels.findIndex(
            (level) => level.order === cLevel.order
        );
        setIndex(index);
        setTimePlay(0);
    };

    /**
     * Updates the simulation data based on the provided value, index, and property name.
     *
     * @param {string} value - The new value to set for the specific property.
     * @param {number} index - The index of the script to update within the current level of the
     *     simulation.
     * @param {string} name - The name of the property to update, which determines which field in
     *     the script object is modified. Acceptable values include:
     *                        - "script": Updates the script text.
     *                        - "hint_text": Updates the hint text.
     *                        - "keywords": Updates the keywords array.
     *                        - "captivate_fileName": Updates the captivate folder name.
     *                        - "overalConfidence": Updates the overall confidence value.
     */
    const handleChange = (value, index, name) => {
        const sim = {...data};
        if (name === "script") {
            sim.levels[currentIndex].scripts[index].script = value;
        } else if (name === "hint_text") {
            sim.levels[currentIndex].scripts[index].hint_text = value;
        } else if (name === "keywords") {
            sim.levels[currentIndex].scripts[index].keywords = value;
        } else if (name === 'captivate_fileName') {
            sim.levels[currentIndex].scripts[index].captivate_folder_name = value;
        } else if (name === 'overalConfidence') {
            sim.levels[currentIndex].scripts[index].overalConfidence = value;
        }
        setData(sim);
    };

    /**
     * Updates the keywords for a specific script at a given index within the current level.
     *
     * @function handleKeywordChange
     * @param {number} index - The index of the script for which the keywords are being updated.
     * @param {Object} event - The event object containing the updated keywords.
     * Updates the `keywords` property of the specified script in the current level.
     * Clones the `data` state, modifies the specific script's keywords, and updates the state with
     *     the new object.
     */
    const handleKeywordChange = (index, event) => {
        const sim = {...data};
        sim.levels[currentIndex].scripts[index].keywords = [...event];
        setData(sim);
    };

    /**
     * Updates the tests for a specific script at the specified index within the levels data
     * structure.
     *
     * @param {number} index - The index of the script to be updated within the scripts array.
     * @param {Array} updatedTests - The updated list of tests to be assigned to the script.
     */
    const handleTestChange = (index, updatedTests) => {
        const sim = {...data};
        sim.levels[currentIndex].scripts[index].tests = updatedTests;
        setData(sim);
    };

    /**
     * Asynchronously handles the generation and updating of audio-related data in the
     * application's state.
     *
     * @param {number} index - The index of the current script in the level's scripts array.
     * @param {string} url - The URL pointing to the generated audio file.
     * @param {string} expiry_date - The expiration date of the audio file.
     * @param {string} key - A unique key or identifier associated with the audio file.
     * @param {string} type - The type or category of the generated audio.
     * @param {string} voice - The voice configuration or identifier used in the audio generation.
     */
    const handleAudioGenerate = async (index, url, expiry_date, key, type, voice) => {
        const sim = {...data};
        sim.levels[currentIndex].scripts[index].agent_recorded_audio = type;
        sim.levels[currentIndex].scripts[index].audio_url = url;
        sim.levels[currentIndex].scripts[index].expiry_date = expiry_date;
        sim.levels[currentIndex].scripts[index].audio_key = key;
        sim.levels[currentIndex].scripts[index].voice = voice;
        setData(sim);
    };

    /**
     * Handles the editing of a level name by capturing the input value from an event.
     * Updates the state with the provided value from the event's target.
     *
     * @param {Object} event - The event object triggered by the input field.
     * @param {Object} event.target - The target element that triggered the event.
     * @param {string} event.target.value - The current value of the input field.
     */
    const handleEditLevel = (event) => {
        const {value} = event.target;
        setLevelName(value);
    };

    /**
     * Asynchronous function to handle saving data related to levels.
     *
     * This function creates a copy of the current data object, modifies the
     * name property of a specific level (based on the current index), and
     * updates the state and other related configurations.
     *
     * Operations performed:
     * - Creates a deep copy of the `data` object and modifies the level name.
     * - Sets the updated data back to state and closes the dialog/state with `setOpen`.
     * - Calls an external function `editLevels` to persist the updated level information.
     * - Resets the `levelName` variable.
     *
     * Dependencies:
     * - Expects certain state variables and functions such as `data`, `currentIndex`,
     *   `levelName`, `setData`, `setOpen`, `editLevels`, and `setLevelName`.
     * - Relies on the `editLevels` function to perform the update operation on the given
     *   level's unique identifier and updated details.
     *
     * Make sure all external dependencies are properly initialized prior to using this function.
     *
     * @async
     * @function handleSave
     */
    const handleSave = async () => {
        const sim = {...data};
        data.levels[currentIndex].name = levelName;
        setData(sim);
        setOpen(false);
        editLevels(data.levels[currentIndex]._id, {
            name: data.levels[currentIndex].name,
            sim_id: data.levels[currentIndex].sim_id,
        });
        setLevelName("");
    };

    /**
     * Updates the `hide_text` property of a specific script in the `sim` object based on the
     * checkbox state.
     *
     * @param {number} index - The index of the script within the `scripts` array in the current
     *     level.
     * @param {Object} event - The event object from the checkbox input.
     * @param {Object} event.target - The target element of the event.
     * @param {boolean} event.target.checked - The current state of the checkbox.
     */
    const handleCheckboxChange = (index, event) => {
        const sim = {...data};
        sim.levels[currentIndex].scripts[index].hide_text = event.target.checked;
        setData(sim);
    };

    /**
     * Updates the real-time feedback state of a specific script within the levels data structure
     * based on the user's radio button selection.
     *
     * @function handleRadioChange
     * @param {number} index - The index of the script within the current level whose feedback
     *     setting is to be updated.
     * @param {Object} event - The event object from the radio button change.
     * @param {Object} event.target - The target element of the change event.
     * @param {string} event.target.value - The value of the radio button ("yes" or other values).
     */
    const handleRadioChange = (index, event) => {
        const sim = {...data};
        sim.levels[currentIndex].scripts[index].real_time_feedback =
            event.target.value === "yes";
        setData(sim);
    };

    /**
     * Asynchronously handles the deletion of a script within a simulation object.
     * Removes the script from the data model and updates the corresponding state.
     *
     * @function
     * @async
     * @param {number} index - The index of the script to be deleted from the scripts array.
     * @param {Object} script - The script object that contains details for deletion.
     * @param {string} script._id - The unique identifier of the script.
     * @param {string} script.sim_id - The ID of the simulation the script belongs to.
     * @throws Will throw an error if the deletion process fails.
     */
    const handleDeleteScript = async (index, script) => {
        const sim = {...data};
        const requestData = {};
        requestData.script_id = script._id;
        requestData.sim_id = script.sim_id;
        await deleteScript(requestData);
        sim.levels[currentIndex].scripts.splice(index, 1);
        setData(sim);
    };

    /**
     * Handles the duplication of a SIM by creating a duplicate based on the provided data.
     * This function prepares the necessary request data, sends the request to duplicate the SIM,
     * and processes the response to update the user interface accordingly based on success or
     * failure.
     *
     * @function
     * @async
     */
    const handleDuplicateSim = async () => {
        const sim = {...data};
        const requestData = {};
        requestData.simId = sim.id;
        const response = await duplicateSim(requestData);
        if (response.data.success) {
            setSuccessAlert(true);
        } else {
            setAlertType("error");
            setAlertMessage("Error in creating duplicate sim. Please try again.");
            setOpenAlert(false);
        }
    };

    /**
     * Handles the close event for an alert component.
     * Prevents the alert from closing when the reason is "clickaway."
     * Otherwise, it sets the alert's open state to false.
     *
     * @param {Object} event - The event object associated with the alert close action.
     * @param {string} reason - The reason for the alert close event; determines whether the alert
     *     should close.
     */
    const handleAlertClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setOpenAlert(false);
    };

    /**
     * A function to handle the close event of a success alert.
     *
     * This function is triggered when a success alert is closed. It checks the reason for the
     * closure and ignores the event if it was caused by a "clickaway" interaction. Otherwise, it
     * resets the success alert state by hiding the alert, clearing the alert type, and clearing
     * the alert message.
     *
     * @param {Object} event - The event object associated with the alert close action.
     * @param {string} reason - A string indicating the reason for the close action.
     */
    const handleSuccessAlertClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSuccessAlert(false);
        setAlertType("");
        setAlertMessage("");
    };

    /**
     * Updates the audio recording status and clears the audio URL for a specific script in the
     * current level.
     *
     * This function modifies the state of the application by resetting the `agent_recorded_audio`
     * status to `false` and setting `audio_url` to an empty string for a specified script at the
     * given index. The current state is cloned to ensure immutability before applying changes.
     *
     * @param {number} index - The index of the script within the current level to be updated.
     */
    const handleAudioChange = (index) => {
        const sim = {...data};
        sim.levels[currentIndex].scripts[index].agent_recorded_audio = false;
        sim.levels[currentIndex].scripts[index].audio_url = "";
        setData(sim);
    };

    /**
     * Updates the agent's recorded audio status for a specific script in the current simulation
     * level.
     *
     * This function takes the specified script index and marks the `agent_recorded_audio` property
     * as true in the current simulation level within the simulation data. After updating the
     * value,
     * it sets the modified simulation data to the state.
     *
     * @function
     * @param {number} index - The index of the script whose `agent_recorded_audio` property is to
     *     be updated.
     */
    const handleAgentRecordedAudio = (index) => {
        const sim = {...data};
        sim.levels[currentIndex].scripts[index].agent_recorded_audio = true;
        setData(sim);
    };

    /**
     * Updates the autopopulate data and timer for a specific script within a
     *      simulation level.
     *
     * This function modifies the `autopopulate_timer` and `autopopulate_data`
     *      properties for a specific script in the simulation's `levels` array.
     *      It iterates through the nested structure of `autopopulate_data` to
     *      remove the `_id` property from each object within the hierarchy.
     *
     * @param {number} index - The index of the script within the `scripts` array
     *                          to update.
     * @param {Object} array - The object containing `autopopulate_data` to be
     *                          set for the script.
     * @param {number} timer - The value of the autopopulate timer to set for
     *                          the script.
     */
    const handleAutopopulateChange = async (index, array, timer) => {
        const sim = {...data},
            clearFieldElements = (field) => {
                field.forEach((element) => delete element._id)
            },
            clearSectionFields = (tab) => {
                tab.sections.forEach((field) => {
                    clearFieldElements(field.fields);
                });
            },
            clearTabs = (tabs) => {
                tabs.forEach((tab) => clearSectionFields(tab));
            };

        sim.levels[currentIndex].scripts[index].autopopulate_timer = timer;
        sim.levels[currentIndex].scripts[index].autopopulate_data = array.autopopulate_data;
        sim.levels[currentIndex].scripts[index].autopopulate_data.forEach(clearTabs);
        setData(sim);
    };

    /**
     * Resets the autopopulate data and timer for a specific script within the
     *      simulation's current level.
     *
     * @function
     * @async
     * @param {number} index - The index of the script within the current level's
     *                          scripts array that needs to be reset.
     * @description Updates the autopopulate-related properties of the script at
     *          the specified index:
     *              - Resets the `autopopulate_timer` to 0.
     *              - Sets the `autopopulate_data` property to the predefined
     *              `tabs` value.
     * The updated simulation data object is then applied using the `setData`
     *      function.
     */
    const handleResetAutopopulate = async (index) => {
        const sim = {...data};
        sim.levels[currentIndex].scripts[index].autopopulate_timer = 0;
        sim.levels[currentIndex].scripts[index].autopopulate_data = tabs;
        setData(sim);
    };

    /**
     * Updates the selected clip identifier.
     *
     * This function sets the currently selected clip by updating its identifier.
     * It is typically used to keep track of a user's selection in a list
     * of clips or media items.
     *
     * @function clipScroll
     * @param {string|number} id - The identifier of the clip to be selected.
     */
    const clipScroll = (id) => {
        setSelectedClip(id);
    };

    /**
     * Handles the drag-and-drop reordering of scripts within a level.
     *
     * This function is triggered after a drag-and-drop action is completed. It updates the order
     * of the scripts based on the new position after the drop. Each script's `order` is updated
     * sequentially, and any script marked as `newScript` has its status reset. The updated data is
     * saved back to the application's state.
     *
     * @param {Object} result - The result object provided by the drag-and-drop library.
     * @param {Object} result.source - The source object containing information about the item being dragged.
     * @param {number} result.source.index - The index of the item before the drag.
     * @param {Object} result.destination - The destination object containing information about where the item is dropped.
     * @param {number} result.destination.index - The index of the item after the drop (new position).
     *
     * @throws {Error} If the destination index is null or undefined.
     */
    const handleOnDragEnd = (result) => {
        const levelScript = {...data};
        const items = Array.from(levelScript.levels[currentIndex].scripts);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        items.map(async (item, index) => {
            item.order = index + 1;
            if (item.newScript) {
                item.newScript = false;
            }
            await addLevelScript(item);
        });
        levelScript.levels[currentIndex].scripts = items;
        setData(levelScript);
    };

    /**
     * The `setTimePlayfun` function updates the state and properties of an audio player
     * and its associated data object. It sets the play status of script objects within
     * the data to either true or false based on the provided `dataa` object. It also updates
     * the audio player state and increments the playback time counter.
     *
     * @function
     * @param {Object} audioPlayer - The audio player instance or object to be updated.
     * @param {Object} dataa - The data object used to identify which script should have its
     *                          play status updated.
     */
    const setTimePlayfun = (audioPlayer, dataa) => {
        data.levels[currentIndex].scripts.map((script) => {
            script.play = (script._id === dataa._id);
            return data;
        });
        setData(data);
        setCurrentAudio(audioPlayer);
        setTimePlay((prev) => prev + 1)
    };

    /**
     * Pauses specific script execution by updating its state.
     *
     * This function iterates over the scripts in the 'levels' property of the provided data,
     * identifies the script that matches the given identifier, and modifies its state
     * to set it as playable. It also resets a timer to zero and updates the data state.
     *
     * @param {Object} dataa - The data object containing the identifier of the script to modify.
     */
    const pauseFunction = (dataa) => {
        data.levels[currentIndex].scripts.map((script) => {
            if (script._id === dataa._id) {
                script.play = true;
                setTimePlay(0);
            }
            return data;
        });
        setData(data);
    };

    /**
     * Handles the drop action for a given drop zone and item in the application.
     *
     * The function processes drag-and-drop operations within the application,
     * dynamically updating data structures based on the action performed. It allows
     * for moving or adding scripts to a specified level in the script system, taking
     * into account whether the action is moving an existing item, adding a new item
     * from the sidebar, or restructuring items within the parent structure.
     *
     * Operations supported include:
     * - Moving new items from the sidebar to a designated script level.
     * - Rearranging scripts both within the same parent level and across levels.
     * - Dynamically creating or updating script-level data, including sending
     *   asynchronous requests to update the backend.
     *
     * @param {Object} dropZone - The target drop zone where the item is being dropped.
     * @param {Object} item - The item being dragged and dropped. It can be an object representing
     *                        an existing script or a new item from the sidebar.
     *
     * @returns {Promise<void>} A promise that resolves when the drop operation and all
     *                          related backend updates are completed.
     *
     * - `dropZone.path`: Specifies the hierarchical path of the drop zone.
     * - `dropZone.type`: Type of the drop zone affecting the addition of new scripts.
     * - `item.character`: Specifies the characteristics of a new sidebar item to be added.
     * - Updates the internal application state to reflect changes made during drag-and-drop
     *   operation.
     */
    const handleDrop = async (dropZone, item) => {
        let splitDropZonePath;
        let pathToDropZone;
        if (dropZone.path) {
            splitDropZonePath = dropZone.path.split("-");
            pathToDropZone = splitDropZonePath.slice(0, -1).join("-");
        }
        const levelScript = {...data};
        // sidebar info
        if ((item === undefined && dropZone.type) || item.character) {
            // 1. Move sidebar item into page
            const scriptData = levelScript.levels[currentIndex];
            const {length} = scriptData.scripts
            if (length === 0 || levelScript.levels[currentIndex]._id) {
                // push the script when double click the character
                if (dropZone.type) {
                    levelScript.levels[currentIndex].scripts.push({
                        order: length,
                        sim_character_id: dropZone._id,
                        script: "",
                        keywords: [],
                        hint_text: "",
                        hide_text: false,
                        audio_url: "",
                        autopopulate_timer: 0,
                        autopopulate_data: [...tabs],
                        status: "saved",
                        real_time_feedback: false,
                        natural_lenguage: data.natural_lenguage,
                        agent_recorded_audio: false,
                        newScript: true,
                    });
                }
                const requestData = {};
                if (
                    (scriptData.scripts.length ===
                        0 ||
                        (dropZone.type && scriptData.scripts.length === 1)) &&
                    !levelScript.levels[currentIndex]._id
                ) {
                    // request data when initial script is added to rescpective level
                    levelScript.levels[currentIndex].scripts =
                        dropZone.type ? levelScript.levels[currentIndex].scripts : [];
                    requestData.is_level = true;
                    requestData.name = levelScript.levels[currentIndex].name;
                    requestData.level_order = levelScript.levels[currentIndex].order;
                    requestData.real_time_feedback =
                        levelScript.levels[currentIndex].real_time_feedback;
                    requestData.natural_lenguage = data.natural_lenguage;
                    requestData.level_hint_text = levelScript.levels[currentIndex].order !== 0;
                } else {
                    // request data when a respective level has more than one script
                    requestData.is_level = false;
                    requestData.sim_level_id = levelScript.levels[currentIndex]._id;
                    requestData.newScript = true;
                }
                requestData.sim_id = levelScript.id;
                requestData.script = "";
                requestData.sim_character_id = dropZone.type ?
                    dropZone._id :
                    item.character._id;
                requestData.audio_url = "";
                requestData.order =
                    dropZone.type ? scriptData.scripts.length.toString() : splitDropZonePath[1];
                requestData.status = "saved";
                requestData.real_time_feedback = false;
                requestData.natural_lenguage = data.natural_lenguage;
                requestData.keywords = [];
                requestData.hint_text = "";
                requestData.hide_text = false;
                requestData.agent_recorded_audio = false;
                requestData.autopopulate_timer = 0;
                requestData.autopopulate_data = tabs;
                const response = await addLevelScript(requestData);
                if (response.sim_level_id) {
                    levelScript.levels[currentIndex]._id = response.sim_level_id;
                }
                if (!dropZone.type) {
                    let items = Array.from(levelScript.levels[currentIndex].scripts);
                    items =
                        handleMoveSidebarComponentIntoParent(data.levels[currentIndex],
                            splitDropZonePath,
                            requestData
                        ).scripts;
                    items[splitDropZonePath[1]]._id = response.sim_script_id;
                    items.map(async (item, index) => {
                        if (items[splitDropZonePath[1]]._id !== item._id) {
                            item.newScript = false;
                        }
                        item.order = index + 1;
                        await addLevelScript(item);
                    });
                    levelScript.levels[currentIndex].scripts = items;
                } else {
                    levelScript.levels[currentIndex].scripts[length]._id = response.sim_script_id;
                    setSelectedClip(response.sim_script_id);
                }
                setData(levelScript);
            }
        } else {
            // move down here since sidebar items dont have path
            const splitItemPath = item.path.split("-");
            const pathToItem = splitItemPath.slice(0, -1).join("-");

            // 2. Pure move (no create)
            if (splitItemPath.length === splitDropZonePath.length) {
                // 2.a. move within parent
                if (pathToItem === pathToDropZone) {
                    let items = Array.from(levelScript.levels[currentIndex].scripts);
                    items =
                        handleMoveWithinParent(data.levels[currentIndex],
                            splitDropZonePath,
                            splitItemPath
                        ).scripts;

                    items.map(async (item, index) => {
                        item.order = index + 1;
                        await addLevelScript(item);
                    });
                    levelScript.levels[currentIndex].scripts = items;
                    setData(levelScript);
                }
            }
        }
    };

    /**
     * Handles changes to level options based on the provided event.
     *
     * @param {Object} evt - The event object containing details about the change.
     * @param {Object} evt.detail - Details of the change event.
     * @param {boolean} evt.detail.success - Indicates if the operation was successful.
     * @param {number} evt.detail.levelIndex - Index of the level being modified.
     * @param {string} evt.detail.option - The specific level option being updated.
     * @param {*} evt.detail.value - The new value of the specified level option.
     *
     * Updates the corresponding value in the data object for the specified level and option.
     * Sets the loading state, alert type, alert message, and manages the alert display accordingly.
     * Resets alert states after 5 seconds.
     */
    const handleLevelOptionChange = (evt) => {
        //console.log('handleLevelOptionChange event = %o', evt);
        if (evt.detail.success) {
            let sim = {...data};
            sim.levels[evt.detail.levelIndex][evt.detail.option] = evt.detail.value;
            setData(sim);

            setLoad(false);
            setAlertType("success");
            setAlertMessage("Successfuly updated " + evt.detail.option + " feature");
            setOpenAlert(true);
        } else {
            setLoad(false);
            setAlertType("error");
            setAlertMessage("Error in changing " + evt.detail.option + " feature");
            setOpenAlert(true);
        }

        setTimeout(
            () => {
                setOpenAlert(false);
                setAlertType("");
                setAlertMessage("");
            },
            5000
        );
    };

    const rowPath = 0;
    return (
        <Paper elevation={0}>
            <TopTableToolbar
                updateData={updateData}
                decreaseStep={decreaseStep}
                increaseStep={increaseStep}
                data={data}
                addLevels={addLevels}
                uploadLevels={uploadLevels}
                newSim={newSim}
                setFlag={setFlag}
                status={status}
                importLevel={() => setImportLevel((prev) => !prev)}
                handleDuplicateSim={handleDuplicateSim}
            />
            <AudioPlaylist audioFiles={audioList}
                            playMode='single'
            />
            <Details currentSim={currentSim} title={newSim.title}/>
            {openAlert ? (
                <Alert
                    variant="filled"
                    onClose={handleAlertClose}
                    severity={alertType ? alertType : 'error'}
                    className={alertType === 'error' ? classes.errorAlert : ''}
                >
                    {alertMesage}
                </Alert>
            ) : null}
            {openSuccessAlert ? (
                <Alert
                    className={classes.alert}
                    variant="filled"
                    onClose={handleSuccessAlertClose}
                    severity="success"
                >
                    Success! Duplicate Sim Created.
                </Alert>
            ) : null}
            <div className={classes.mainDiv}>
                {isLoading ? (
                    <div className={classes.loaderDiv}>
                        <CircularProgress color="primary"/>
                    </div>
                ) : null}

                <Grid container className={classes.gridContainer}>
                    <Grid item xs={2} sm={2} lg={2} xl={2}>
                        <Levels
                            simGenerated={simGenerated}
                            Level={Level}
                            levels={data.levels}
                            passFailLevel={data?.is_pass_fail_criteria_set ?
                                data?.pass_fail_id?.sim_level_id :
                                null}
                            data={data}
                            setData={setData}
                            newSim={newSim}
                            updateData={updateData}
                            deleted={deleted}
                            flag={flag}
                            setFlag={setFlag}
                            setDeleted={setDeleted}
                            setIndex={setIndex}
                            onOptionChange={handleLevelOptionChange}
                            setAlertType={setAlertType}
                            setAlertMessage={setAlertMessage}
                            setOpenAlert={setOpenAlert}
                            setLoad={setLoad}
                            load={load}
                            setGeneratingAudio={setGeneratingAudio}
                        />
                    </Grid>
                    <DndProvider backend={HTML5Backend}>
                        <Grid item className={classes.editCanvas} sm={8} lg={8} xl={8} xs={7}>
                            <Toolbar className={classes.toolBar}>
                                <Typography className={classes.canvasLabel}>
                                    {currentIndex + 1}.{" "}
                                    {data.levels[currentIndex]
                                        ? data.levels[currentIndex].name
                                        : ""}
                                    <Button
                                        disabled={
                                            !(
                                                data.levels &&
                                                data.levels.length > 0 &&
                                                data.levels[currentIndex]
                                            )
                                        }
                                        className={classes.editBtn}
                                        color="default"
                                        onClick={handleClickOpen}
                                        startIcon={<EditIcon className={classes.editIcon}/>}
                                    />
                                </Typography>
                                <Dialog
                                    open={open}
                                    onClose={handleClose}
                                    aria-labelledby="levelName"
                                    fullWidth
                                    maxWidth="sm"
                                >
                                    <DialogTitle id="LevelName">Edit Level Name</DialogTitle>
                                    <DialogContent>
                                        <TextField
                                            autoFocus
                                            id="LevelName"
                                            margin="dense"
                                            name="name"
                                            multiline
                                            value={levelName}
                                            onChange={(e) => handleEditLevel(e)}
                                            fullWidth
                                        />
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={handleClose}
                                                className={classes.cancelButton}>
                                            Cancel
                                        </Button>
                                        <Button onClick={handleSave} color="primary">
                                            Save
                                        </Button>
                                    </DialogActions>
                                </Dialog>
                                <GenerateSim
                                    currentIndex={currentIndex}
                                    data={data}
                                    setTotalLevel={setTotalLevel}
                                    totalLevel={totalLevel}
                                    setSimGenerated={setSimGenerated}
                                    setLevelSelect={setLevelSelect}
                                    levelSelect={levelSelect}
                                    handleClose={handleClose}
                                    setLoad={setLoad}
                                    setFlag={setFlag}
                                    setAlertType={setAlertType}
                                    setAlertMessage={setAlertMessage}
                                    setOpenAlert={setOpenAlert}
                                />
                                <div>
                                    <LevelOptions
                                        data={data}
                                        setData={setData}
                                        levelIndex={currentIndex}
                                        singleLevel={data?.levels[currentIndex] ?? null}
                                        passFailLevel={data?.is_pass_fail_criteria_set ?
                                            data?.pass_fail_id?.sim_level_id :
                                            ''}
                                        classes={classes}
                                        onOptionChange={handleLevelOptionChange}
                                        setAlertType={setAlertType}
                                        setAlertMessage={setAlertMessage}
                                        setOpenAlert={setOpenAlert}
                                        updateData={updateData}
                                        setLoad={setLoad}
                                        load={load}
                                    />
                                </div>
                            </Toolbar>
                            <div className={classes.progress}>
                                {load ? <LinearProgress color="primary"/> : null}
                            </div>
                            <div>
                                {data?.levels?.[currentIndex]?.scripts?.map((clip, i) => {
                                    let currentChar;
                                    const agentCharIndex = data?.sim_characters?.findIndex(char => char.type === 'agent');
                                    const customerCharIndex = data?.sim_characters?.findIndex(char => char.type === 'customer');
                                    const otherCharIndex = data?.sim_characters?.findIndex(char => char.type === 'other');

                                    if (data?.sim_characters[agentCharIndex]?._id === clip.sim_character_id) {
                                        currentChar = (<span key={clip._id}>
                                            <Tooltip key={clip._id}
                                                     title={clip.script}
                                            >
                                                <MicOutlinedIcon className={classes.agentMicIcon}
                                                                 onClick={() => clipScroll(clip._id)}
                                                />
                                            </Tooltip>
                                            {(i + 1) % 50 === 0 ? <br></br> : ''}
                                        </span>);

                                        if (clip.agent_recorded_audio) {
                                            currentChar = (<span key={clip._id}>
                                                <Tooltip key={clip._id}
                                                         title={clip.script}
                                                >
                                                    <VolumeUpIcon className={classes.agentMicIcon}
                                                                  onClick={() => clipScroll(clip._id)}
                                                    />
                                                </Tooltip>
                                                {(i + 1) % 50 === 0 ? <br></br> : ''}
                                            </span>);
                                        }
                                    } else if (data?.sim_characters[customerCharIndex]?._id === clip.sim_character_id) {
                                        currentChar = (<span key={clip._id}>
                                            <Tooltip title={clip.script}>
                                                <VolumeUpIcon className={classes.customerMicIcon}
                                                              onClick={() => clipScroll(clip._id)}
                                                />
                                            </Tooltip>
                                            {(i + 1) % 50 === 0 ? <br></br> : ''}
                                        </span>);
                                    } else if (data?.sim_characters[otherCharIndex]?._id === clip.sim_character_id) {
                                        currentChar = (<span key={clip._id}>
                                            <Tooltip title={clip.script}>
                                                <VolumeUpIcon className={classes.otherMicIcon}
                                                              onClick={() => clipScroll(clip._id)}
                                                />
                                            </Tooltip>
                                            {(i + 1) % 50 === 0 ? <br></br> : ''}
                                        </span>);
                                    } else {
                                        currentChar = (<span key={clip._id}>
                                            <Tooltip title={clip.script}>
                                                <VolumeUpIcon className={classes.coachMicIcon}
                                                              onClick={() => clipScroll(clip._id)}
                                                />
                                            </Tooltip>
                                            {(i + 1) % 50 === 0 ? <br></br> : ''}
                                        </span>);
                                    }
                                    return currentChar
                                })}
                            </div>
                            <div
                                className={
                                    data?.levels?.length > 0 &&
                                    data?.levels?.[currentIndex] &&
                                    data?.levels?.[currentIndex]?.scripts?.length === 0
                                        ? classes.scriptDiv1
                                        : classes.scriptDiv
                                }
                            >
                                {data?.levels?.length > 0 &&
                                data?.levels?.[currentIndex] &&
                                data?.levels?.[currentIndex]?.scripts?.length === 0 ? (
                                    <section className={classes.backgroundImage}>
                                        <DropZone
                                            data={{
                                                path: `${rowPath}-${data.levels[currentIndex].scripts.length}`,
                                                childrenCount: data.levels[currentIndex].scripts.length
                                            }}
                                            onDrop={handleDrop}
                                            isLast
                                        />
                                    </section>
                                ) : null}
                                <DragDropContext onDragEnd={handleOnDragEnd}>
                                    <Droppable droppableId="characters">
                                        {(provided) => (
                                            <div
                                                className="characters"
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                            >
                                                {data?.levels?.length > 0 &&
                                                    data?.levels?.[currentIndex] &&
                                                    data?.levels?.[currentIndex]?.scripts?.map( (script, index) => {
                                                        const currentPath = `${rowPath}-${index}`;
                                                        return (
                                                            <Draggable
                                                                key={script._id}
                                                                draggableId={script._id}
                                                                index={index}
                                                            >
                                                                {(provided) => (
                                                                    <div
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                    >
                                                                        <DropZone
                                                                            data={{
                                                                                path: currentPath,
                                                                                childrenCount: data.levels[currentIndex].scripts.length
                                                                            }}
                                                                            onDrop={handleDrop}
                                                                        />
                                                                        <div
                                                                            ref={selectedClip ===
                                                                            script._id ?
                                                                                clipRef :
                                                                                null}>
                                                                            <CharacterData
                                                                                path={currentPath}
                                                                                onlyKeywords={data.levels[currentIndex].only_keywords}
                                                                                key={index}
                                                                                index={index}
                                                                                data={script}
                                                                                sim={data}
                                                                                tabs={tabs}
                                                                                setTimePlayfun={setTimePlayfun}
                                                                                currentAudio={currentAudio}
                                                                                timePlay={timePlay}
                                                                                pauseFunction={pauseFunction}
                                                                                characterData={data.sim_characters}
                                                                                handleChange={handleChange}
                                                                                handleAudioChange={handleAudioChange}
                                                                                handleKeywordChange={handleKeywordChange}
                                                                                handleTestChange={handleTestChange}
                                                                                handleCheckboxChange={handleCheckboxChange}
                                                                                handleAudioGenerate={handleAudioGenerate}
                                                                                handleRadioChange={handleRadioChange}
                                                                                handleDeleteScript={handleDeleteScript}
                                                                                handleAgentRecordedAudio={handleAgentRecordedAudio}
                                                                                handleAutopopulateChange={handleAutopopulateChange}
                                                                                handleResetAutopopulate={handleResetAutopopulate}
                                                                                updateData={updateData}
                                                                                borderColor={getBackground(script.type)}
                                                                                levelOrder={data.levels[currentIndex].order}
                                                                                newSim={newSim}
                                                                                currentVoice={
                                                                                    newSim.sim_characters.filter( (char) => (char._id === script.sim_character_id) ).map(char => char.voice)
                                                                                }
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        )}
                                                    )
                                                }
                                                {provided.placeholder}
                                            </div>
                                        )}

                                    </Droppable>
                                </DragDropContext>
                                <div>
                                    {
                                        (data?.levels?.length > 0) &&
                                        data?.levels?.[currentIndex] &&
                                        data?.levels?.[currentIndex]?.scripts &&
                                        <DropZone
                                            data={{
                                                path: `${rowPath}-${data.levels[currentIndex].scripts.length}`,
                                                childrenCount: data.levels[currentIndex].scripts.length
                                            }}
                                            onDrop={handleDrop}
                                            isLast
                                        />
                                    }
                                </div>
                                {
                                    importLevel &&
                                    (data?.levels?.[currentIndex]?.scripts?.length === 0) ?
                                        <div className={classes.loaderDiv}>
                                            <CircularProgress color="primary"/>
                                        </div> :
                                        ''
                                }
                            </div>
                        </Grid>
                        <Grid
                            className={
                                data?.levels &&
                                (data?.levels?.length > 0) &&
                                data?.levels?.[currentIndex] &&
                                (data?.levels?.[currentIndex]?.scripts?.length === 0) ?
                                    classes.intialCharacterMainGrid :
                                    classes.finalCharacterMainGrid
                            }
                            item
                            xs={2} lg={2} xl={2} sm={2}
                            key="card"
                        >
                            <Grid
                                container
                                spacing={1}
                                direction="row"
                                justifyContent="flex-start"
                                alignItems="flex-start"
                            >
                                <Typography className={classes.characterLabel}>
                                    Characters
                                </Typography>
                                {data.sim_characters.map((character, index) => (
                                    <Characters
                                        bgColor={getBackground(character.type)}
                                        avatar={character.avatar}
                                        key={index}
                                        character={character}
                                        showDetails={() => handleDrop(character)}
                                    />
                                ))}
                            </Grid>
                        </Grid>
                    </DndProvider>
                </Grid>
            </div>
        </Paper>
    );
};

/**
 * PropTypes definitions for the CreateLevels component.
 *
 * This specifies the expected types and requirements for the props
 * that the CreateLevels component should receive.
 *
 * Properties:
 * - levels: Defines the structure and type of levels expected as input data.
 * - onUpdate: Callback function triggered when levels are modified.
 * - maxLevels: Specifies the maximum number of levels allowed.
 * - isEditable: Boolean indicating if levels can be edited.
 * - customStyles: Object to define styling overrides for the component.
 */
CreateLevels.propTypes = {
    newSim: PropTypes.object,
    increaseStep: PropTypes.func,
    decreaseStep: PropTypes.func,
    currentSim: PropTypes.object,
    levels: PropTypes.array,
    fetchCurrentSim: PropTypes.func,
    addLevels: PropTypes.func,
    uploadLevels: PropTypes.func,
    fetchAutoPopulate: PropTypes.func,
    tabs: PropTypes.array,
    fetchSimDetails: PropTypes.func,
    status: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
    generatingAudio: PropTypes.bool,
};

/**
 * Maps the relevant portions of the Redux state to component props.
 *
 * @param {Object} state - The entire Redux state object.
 * @returns {Object} An object containing the mapped state properties:
 * - `status`: Represents the current simulation's status.
 * - `newSim`: Indicates whether a new simulation is being created.
 * - `currentSim`: The data for the currently selected simulation.
 * - `levels`: The levels associated with the simulation.
 * - `characters`: The character list tied to the simulation.
 * - `tabs`: Represents auto-populated tab configurations.
 */
const mapStateToProps = (state) => ({
    status: state.sim.status,
    newSim: state.sim.newSim,
    currentSim: state.sim.currentSim,
    levels: state.sim.levels,
    characters: state.sim.characters,
    tabs: state.sim.autoPopulate,
});

/**
 * An object mapping dispatchable action creators to props for use in a React-Redux container.
 * This allows the connected component to trigger specific state changes and side effects
 * by dispatching actions to the Redux store.
 *
 * Properties:
 * - `increaseStep`: Triggers an action to increment the current step in the state.
 * - `decreaseStep`: Triggers an action to decrement the current step in the state.
 * - `fetchCurrentSim`: Dispatches an action to retrieve the current simulation data.
 * - `fetchCharacterList`: Dispatches an action to retrieve the list of available characters.
 * - `addLevels`: Dispatches an action to add levels to the current state.
 * - `addNewLevels`: Dispatches an action to add new levels to the system.
 * - `uploadLevels`: Dispatches an action to upload level-related data to the server or state.
 * - `fetchAutoPopulate`: Dispatches an action to fetch data for auto-population.
 * - `fetchSimDetails`: Dispatches an action to retrieve details of a specific simulation.
 */
const mapDispatchToProps = {
    increaseStep,
    decreaseStep,
    fetchCurrentSim,
    fetchCharacterList,
    addLevels,
    addNewLevels,
    uploadLevels,
    fetchAutoPopulate,
    fetchSimDetails,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateLevels);
